import { PencilSquareIcon, PlusIcon } from "@heroicons/react/20/solid";
import { forwardRef, memo } from "react";
import { OtsIconActionButton } from "../components/OtsButton";
import { AuditScoreRing } from "../audit/AuditScoreRing";
import { ActivityDayTimeline } from "./ActivityDayTimeline";
import { ActivityDriverHeader, type ActivitySelectionState } from "./ActivityDriverHeader";
import { ActivityEntryRow } from "./ActivityEntryRow";
import { ActivityShiftCostCell } from "./ActivityShiftCostCell";
import { ActivityShiftRow } from "./ActivityShiftRow";
import type { ActivityShiftActionId, ActivityShiftTarget } from "./driver-activity-actions";
import { activityMutedClass } from "./driver-activity-layout";
import { type ActivityDriver, type ActivityEntry, isActivityTicket, lowestAudit } from "./driver-activity-view-model";
import { type DriverActivityRowSettings, DriverActivityRowSettingsProvider, useDriverActivityRowSettings } from "./DriverActivityRowContext";

/** A driver's card; shared settings come from the list (`DriverActivityRowSettings`), and any given here win. */
export type ActivityDriverSectionProps = DriverActivityRowSettings & {
  driver: ActivityDriver;
  isCollapsed?: boolean;
  /** Position in the list, for a virtualizer measuring the card. */
  index?: number;
};

const NONE: ReadonlySet<string> = new Set();

const shiftActionButton = (
  actionId: ActivityShiftActionId,
  target: ActivityShiftTarget,
  onShiftAction: (actionId: ActivityShiftActionId, target: ActivityShiftTarget) => void,
) => {
  if (actionId === "edit-shift" && !target.shift) return null;
  const label = actionId === "edit-shift" ? "Edit shift" : target.shift ? "New shift after this one" : "Create a shift for this activity";
  return (
    <OtsIconActionButton
      key={actionId}
      variant="text"
      size="compact"
      icon={actionId === "edit-shift" ? <PencilSquareIcon/> : <PlusIcon/>}
      label={label}
      onClick={() => onShiftAction(actionId, target)}/>
  );
};

const selectionOf = (tickets: ActivityEntry[], selected: ReadonlySet<string>): ActivitySelectionState => {
  const count = tickets.filter((entry) => selected.has(entry.key)).length;
  return count === 0 ? "none" : count === tickets.length ? "all" : "some";
};

/** One driver's card: their header, then each shift as a block from Start Shift to End Shift. */
export const ActivityDriverSection = memo(forwardRef<HTMLElement, ActivityDriverSectionProps>(function ActivityDriverSection({
  driver,
  isCollapsed = false,
  index,
  ...overrides
}, ref) {
  const settings = useDriverActivityRowSettings(overrides);
  const {
    timeZone,
    showRevenue = false,
    showAudit = false,
    auditScoreType = "BUSINESS_VALIDATION_CONFIDENCE",
    showTimeline = false,
    onToggleDriver,
    selected = NONE,
    onSelectEntries,
    activeKey,
    onActivate,
    rowIdPrefix = "",
    day,
    now,
    shiftActionIds,
    onShiftAction,
    onOpenDriverMap,
    rateModelsNarrowed,
    shiftCost,
  } = settings;
  const tickets = driver.shifts.flatMap((shift) => shift.entries).filter(isActivityTicket);
  const selection = selectionOf(tickets, selected);

  return (
    <section
      ref={ref}
      data-index={index}
      data-driver={driver.key}
      role="rowgroup"
      aria-label={driver.name}
      // `clip`, not `hidden`: rounds the card without becoming a scroll container, so the header stays sticky.
      className="mx-3 mt-4 overflow-clip rounded-lg border border-[var(--ots-outline)] bg-[var(--ots-background)] shadow-sm">
      {/* 52px = the two column-header tiers above. */}
      <ActivityDriverHeader
        driver={driver}
        collapsed={isCollapsed}
        onToggle={() => onToggleDriver?.(driver.key)}
        onOpenMap={onOpenDriverMap && (() => onOpenDriverMap(driver.key))}
        showRevenue={showRevenue}
        selection={selection}
        onToggleSelection={onSelectEntries && (() => onSelectEntries(tickets, selection !== "all"))}
        audit={showAudit && tickets.some((entry) => entry.audit?.hasAuditRun) && (
          <AuditScoreRing audit={lowestAudit(tickets, auditScoreType)} scoreType={auditScoreType} aggregate timeZone={timeZone}/>
        )}
        timeline={showTimeline && <ActivityDayTimeline driver={driver} timeZone={timeZone} now={now} activeKey={activeKey} onSelect={onActivate}/>}
        className="sticky top-[52px] z-10"/>
      {!isCollapsed && <DriverActivityRowSettingsProvider value={settings}><div role="presentation" className="flex flex-col gap-3 p-2">
      {driver.shifts.map((group) => (
        <div key={group.key} role="presentation" data-shift-block className="overflow-clip rounded-md border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] shadow-xs">
          {/* Activity outside any shift gets its own start row when it sits beside shifts, or can be given one. */}
          {(group.shift || driver.shifts.length > 1 || (onShiftAction && shiftActionIds?.includes("new-shift"))) && (
            <ActivityShiftRow
              variant="start"
              shift={group.shift}
              timeZone={timeZone}
              day={day}
              now={now}
              audit={showAudit && group.entries.some((entry) => entry.audit?.hasAuditRun) && (
                <AuditScoreRing audit={lowestAudit(group.entries, auditScoreType)} scoreType={auditScoreType} aggregate timeZone={timeZone}/>
              )}
              actions={onShiftAction && shiftActionIds?.length
                ? shiftActionIds.map((actionId) => shiftActionButton(actionId, { shift: group.shift, entries: group.entries }, onShiftAction))
                : undefined}/>
          )}
          {group.entries.map((entry) => (
            <ActivityEntryRow
              key={entry.key}
              id={`${rowIdPrefix}${entry.key}`}
              entry={entry}
              isSelected={selected.has(entry.key)}
              isActive={activeKey === entry.key}/>
          ))}
          {!group.entries.length && (
            <p className={["border-b border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] px-3 py-2 text-xs", activityMutedClass].join(" ")}>
              No activity to show in this shift.
            </p>
          )}
          <ActivityShiftRow
            variant="end"
            shift={group.shift}
            totals={group.totals}
            timeZone={timeZone}
            day={day}
            now={now}
            showRevenue={showRevenue}
            rateModelsNarrowed={rateModelsNarrowed}
            // Only a closed shift can be costed, as in WaterGo, whose end row appears once the shift ends.
            costing={shiftCost && group.shift?.endDttm != null && <ActivityShiftCostCell shift={group.shift} shiftCost={shiftCost}/>}/>
        </div>
      ))}
      </div></DriverActivityRowSettingsProvider>}
    </section>
  );
}));
